import moment from 'moment';
// constants 
import { 
    DEPOSITS_RENEWAL_SCHEDULED,
    RENEWAL_DEPOSIT_HIRING_FLOW,
    RENEWAL_DEPOSIT_HIRING_FLOW_POST_EXPIRATION, 
    DEPOSITS_FLOW_IDS, 
    EXPIRED_RENEWAL_OFFERS_LANDING,
    RENEWAL_OFFERS_LANDING
} from '../../deposits/constants/depositsConstants';
import { RENEWAL_DEPOSIT_EXTENDED_COUNTRIES, RENEWAL_DEPOSIT_ORDER_COUNTRIES } from 'constants/flagBasedFeatures';

const isPostOffer = (flowId) => {
    return flowId === RENEWAL_DEPOSIT_HIRING_FLOW_POST_EXPIRATION || flowId === EXPIRED_RENEWAL_OFFERS_LANDING;
}

const formatOfferData = (offer, isRobinson, contractNumber, index) => {
    const contracts = offer.getIn(['metadata', 'contracts']);
    const contract = contracts.find(contract => contract.get('contractNumber') === contractNumber);
    const flowId = offer.get('flowId');
    const isPostRenovation = isPostOffer(flowId);
    const date = isPostRenovation ? contract.get('offerExpirationDate') : contract.get('closestExpirationDate');
    const amount = contract.get('amount');
    const diffDates = moment(date).diff(moment().startOf('day'), 'days', true);
    return {
        tin: offer.get('tin'),
        tae: offer.get('tae'),
        flowId,
        productId: offer.get('productId'),
        amount,
        months: offer.getIn(['offersValues', 'months']),
        expirationDate: date,
        daysLeft: diffDates,
        isPostRenovation,
        isRobinson,
        examplesIndex: flowId !== DEPOSITS_RENEWAL_SCHEDULED ? index : undefined,
    }
}

const getClosestExpirationDate = (contracts) => {
    return contracts.reduce((acc, contract) => {
        const contractDate = contract.get('closestExpirationDate');
        return moment(contractDate).diff(moment(acc)) < 0 ? contractDate : acc;
    }, moment().add(900, 'years'));
}

const sortOffers = (offers) => {
    return offers.sort((firstOffer, secondOffer) => {
        const firstOfferContracts = firstOffer.getIn(['metadata', 'contracts']);
        const closestExpiringContractDateFirstOffer = getClosestExpirationDate(firstOfferContracts);
        
        const secondOfferContracts = secondOffer.getIn(['metadata', 'contracts']);
        const closestExpiringContractDateSecondOffer = getClosestExpirationDate(secondOfferContracts);
        
        return moment(closestExpiringContractDateFirstOffer).diff(moment(closestExpiringContractDateSecondOffer));
    });
}

const validateOffers = (offers) => {
    return offers && offers.every(offer => DEPOSITS_FLOW_IDS.includes(offer.get('flowId')));
}

const checkImpositionsData = (immDeposits, contractNumber) => {
    const deposit = immDeposits.get('byId').find(deposit => deposit.getIn(['contract', 'contractNumber']) === contractNumber);
    return deposit && !!deposit.get('success');
}

const checkMultiImposition = (immDeposits, contractNumber) => {
    const deposit = immDeposits.get('byId').find(deposit => deposit.getIn(['contract', 'contractNumber']) === contractNumber);
    return !!deposit && !!deposit.get('success') && deposit.getIn(['impositionList', 'byOrder']).size > 1;    
}

const checkAllContractsAreMultiImposition = (offer, immDeposits) => {
    const contractNumbers = offer.getIn(['metadata', 'contracts']);
    return contractNumbers.every(contract => checkMultiImposition(immDeposits, contract.get('contractNumber')));
}

const evaluateOfferEligibility = (flowId, featureFlags) => {
    if (flowId === DEPOSITS_RENEWAL_SCHEDULED) {
        return true;
    } else if (flowId === RENEWAL_DEPOSIT_HIRING_FLOW_POST_EXPIRATION || flowId === EXPIRED_RENEWAL_OFFERS_LANDING) {
        return featureFlags ? featureFlags.get(RENEWAL_DEPOSIT_EXTENDED_COUNTRIES) : true;
    } else if (flowId === RENEWAL_DEPOSIT_HIRING_FLOW || flowId === RENEWAL_OFFERS_LANDING) {
        return featureFlags ? featureFlags.get(RENEWAL_DEPOSIT_ORDER_COUNTRIES) : true;
    }
}

const getExampleData = (offer) => {
    const offerValues = offer.get('offersValues');
    return {
        cancelMonths: offerValues.get('cancelMonths'),
        cancelCost: offerValues.get('cancelCost'),
        exampleInterestAmountCancelled: offerValues.get('exampleInterestAmountCancelled'),
        exampleInterestAmount: offerValues.get('exampleInterestAmount'),
        exampleAmount: offerValues.get('exampleAmount'),
        grossInterest: offerValues.get('grossInterest'),
        tin: offer.get('tin'),
        tae: offer.get('tae'),
        months: offerValues.get('months'),
    }
}

const processOffer = (offer, index, isRobinson, acc, immDeposits, shouldAddExample) => {
    const contractNumbers = offer.getIn(['metadata', 'contracts']);

    contractNumbers.forEach(contract => {
        const contractNumber = contract.get('contractNumber');
        const haveImpositionsData = checkImpositionsData(immDeposits, contractNumber);
        if (haveImpositionsData) {
            acc.offers[contractNumber] = acc.offers[contractNumber]
                ? [...acc.offers[contractNumber], formatOfferData(offer, isRobinson, contractNumber, index)]
                : [formatOfferData(offer, isRobinson, contractNumber, index)];
        }
    });
    if (shouldAddExample) {
        acc.examples.push(getExampleData(offer));
    }
    return acc;
}

const offerHasImpositionsData = (offer, immDeposits) => {
    const contractNumbers = offer.getIn(['metadata', 'contracts']);
    return contractNumbers.some(contract => checkImpositionsData(immDeposits, contract.get('contractNumber')));
}



const processApiData = (data, featureFlags, immDeposits) => {
    const offers = data.getIn(['data', 'offers']) || data;
    let processedOffers;
    if (!validateOffers(offers)) {
        processedOffers = null;
    } else {
        const sortedOffers = offers && sortOffers(offers);

        let index = 0;
        processedOffers = sortedOffers.reduce((acc, offer) => {
            
            if (evaluateOfferEligibility(offer.get('flowId'), featureFlags, immDeposits)) {
                const allContractsAreMultiImposition = checkAllContractsAreMultiImposition(offer, immDeposits);
                const shouldAddExample = offer.get('flowId') !== DEPOSITS_RENEWAL_SCHEDULED && offerHasImpositionsData(offer, immDeposits) && !allContractsAreMultiImposition;
                if (shouldAddExample) {    
                    index++
                }
                const isRobinson = !data.getIn(['data', 'isClientAcceptingOffers']);
                return processOffer(offer, index, isRobinson, acc, immDeposits, shouldAddExample);
            }
            return acc;
        }, { offers: {}, examples: [] });
    }
    return processedOffers;
}

const offersDepositAdapter = ({ offers, featureFlags = null, immDeposits }) => {
    if (!offers) {
        return null;
    }

    const processedOffers = processApiData(offers, featureFlags, immDeposits);
    return processedOffers;
}

export {offersDepositAdapter}